import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { useAuthStore } from '@/stores/authStore';

export const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [niftySpot, setNiftySpot] = useState<{
    price: number;
    change: number;
    change_pct: number;
    source?: string;
  } | null>(null);

  const [indiaVix, setIndiaVix] = useState<{
    price: number;
    change: number;
    change_pct: number;
  } | null>(null);

  const [niftyFuture, setNiftyFuture] = useState<{
    price: number;
    change: number;
    change_pct: number;
    expiry?: string;
  } | null>(null);

  // NOTE (US port): the /api/screening/indices poll was removed with the
  // screening backend. bankNifty/sensex stay null until the US header ticker
  // work (port plan Phase 5) replaces these with SPX / ES-proxy / VIX.
  const [bankNifty] = useState<{
    price: number;
    change: number;
    change_pct: number;
  } | null>(null);

  const [sensex] = useState<{
    price: number;
    change: number;
    change_pct: number;
  } | null>(null);

  // Load sidebar state from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('sidebarOpen');
    if (saved !== null) {
      setSidebarOpen(JSON.parse(saved));
    }
  }, []);

  // Save sidebar state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarOpen', JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  // ✅ NEW: Poll for broker status globally to keep Header in sync
  const { checkBrokerStatus } = useAuthStore();
  useEffect(() => {
    // Initial check
    checkBrokerStatus();

    // Poll every 30 seconds
    const interval = setInterval(() => {
      checkBrokerStatus();
    }, 30000);

    return () => clearInterval(interval);
  }, [checkBrokerStatus]);

  // WebSocket for NIFTY spot price (global across all pages)
  useEffect(() => {
    let ws: WebSocket | null = null;
    let retryCount = 0;
    const maxRetries = 10;
    let retryTimeout: NodeJS.Timeout | null = null;
    let mounted = true;

    const connectWebSocket = () => {
      // Don't retry if unmounted or max retries reached
      if (!mounted || retryCount >= maxRetries) {
        if (retryCount >= maxRetries) {
          console.error('Max WebSocket retry attempts reached. Please refresh page.');
        }
        return;
      }

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/api/ws/nightingale?token=${localStorage.getItem('platform_token') || ''}`;

      try {
        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          retryCount = 0; // Reset retry count on successful connection
          ws?.send(JSON.stringify({ type: 'subscribe' }));
        };

        ws.onmessage = (event) => {
          const message = JSON.parse(event.data);

          if (message.type === 'nifty_spot') {
            setNiftySpot({
              price: message.price,
              change: message.change || 0,
              change_pct: message.change_pct || 0,
              source: message.source,
            });
          } else if (message.type === 'india_vix') {
            setIndiaVix({
              price: message.price,
              change: message.change || 0,
              change_pct: message.change_pct || 0,
            });
          } else if (message.type === 'nifty_future') {
            setNiftyFuture({
              price: message.price,
              change: message.change || 0,
              change_pct: message.change_pct || 0,
              expiry: message.expiry,
            });
          }
          // NOTE: 'us_index' messages (QQQ/DIA proxy chips) are ignored —
          // the header shows only S&P 500 + VIX.
        };

        ws.onerror = () => {
          // Silently handle errors (will retry on close)
        };

        ws.onclose = () => {
          if (!mounted) return;

          // Exponential backoff: 1s, 2s, 4s, 8s, ..., max 30s
          retryCount++;
          const backoffMs = Math.min(1000 * Math.pow(2, retryCount - 1), 30000);

          console.log(`WebSocket disconnected. Retrying in ${backoffMs / 1000}s (attempt ${retryCount}/${maxRetries})`);

          retryTimeout = setTimeout(() => {
            connectWebSocket();
          }, backoffMs);
        };

      } catch (error) {
        console.error('WebSocket connection error:', error);
        // Retry on error
        retryCount++;
        const backoffMs = Math.min(1000 * Math.pow(2, retryCount - 1), 30000);
        retryTimeout = setTimeout(() => {
          connectWebSocket();
        }, backoffMs);
      }
    };

    // Start connection
    connectWebSocket();

    return () => {
      mounted = false;
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        ws.close();
      }
    };
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <Header
          onToggleSidebar={toggleSidebar}
          sidebarOpen={sidebarOpen}
          niftySpot={niftySpot}
          indiaVix={indiaVix}
          niftyFuture={niftyFuture}
          bankNifty={bankNifty}
          sensex={sensex}
        />

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
